import type {
  AssistantChatMessage,
  ToolChatMessage,
  UserChatMessage,
} from "@repo/app-types/entities/chat";
import type {
  BasicTeammate,
  CompanyTeammateListEntry,
  Teammate,
  TeammateCopilotDetails,
  TeammateId,
  TeammatePatchPayload,
} from "@repo/app-types/entities/teammate";
import type { ClientChatMessage } from "@repo/ui/chat/types";
import type { RootState } from "../../app/store.js";
import {
  selectTeammate,
  setTeammate,
} from "../../features/teammate/teammateSlice.js";
import {
  buildListTag,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

interface UpdateTeammateRequest {
  id: TeammateId;
  patchPayload: TeammatePatchPayload;
}

interface CopilotMessagesRequest {
  teammateId: TeammateId;
  chatDetails: TeammateCopilotDetails;
}

function toClientChatMessage(
  msg: UserChatMessage | AssistantChatMessage | ToolChatMessage,
): ClientChatMessage {
  if (msg.role === "tool") {
    return {
      id: msg.id,
      role: msg.role,
      content: msg.content,
      alternativeContent: msg.alternative_content,
      createdAt: msg.created_at,
      toolCallId: msg.tool_call_id,
      inReplyToId: msg.in_reply_to_id,
    };
  }
  return {
    id: msg.id,
    role: msg.role,
    content: msg.content || "",
    createdAt: msg.created_at,
  };
}

const teammateApi = api.injectEndpoints({
  endpoints: (builder) => ({
    allTeammates: builder.query<
      Teammate[],
      { includeDeactivated?: boolean } | void,
      { teammates: Teammate[] }
    >({
      query: (args) => ({
        method: "GET",
        url: "/teammates/",
        params: args?.includeDeactivated
          ? { include_deactivated: "true" }
          : undefined,
      }),
      transformResponse: (response) => response.teammates,
    }),

    basicTeammate: builder.query<
      BasicTeammate,
      TeammateId,
      { data: { teammate: BasicTeammate } }
    >({
      query: (id) => ({
        method: "GET",
        url: `/teammates/${encodeURIComponent(id)}`,
      }),
      transformResponse: (response) => response.data.teammate,
      providesTags: (_result, _error, id) =>
        getTagsProvidedByEntity(TagType.BasicTeammate, id),
    }),

    teammate: builder.query<
      Teammate,
      TeammateId,
      { data: { teammate: Teammate } }
    >({
      query: (id) => ({
        method: "GET",
        url: `/teammates/${encodeURIComponent(id)}`,
      }),
      transformResponse: (response) => response.data.teammate,
      providesTags: (_result, _error, id) =>
        getTagsProvidedByEntity(TagType.Teammate, id),
      // Warm the basicTeammate cache from the same response so consumers using
      // useBasicTeammateQuery don't refetch the same /teammates/:id endpoint.
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertBasicTeammateQueryData(data));
        } catch {
          // The original query already surfaces the error to its caller.
        }
      },
    }),

    updateTeammate: builder.mutation<
      Teammate,
      UpdateTeammateRequest,
      { data: { teammate: Teammate } }
    >({
      query: ({ id, patchPayload }) => ({
        method: "PATCH",
        url: `/teammates/${encodeURIComponent(id)}`,
        body: patchPayload,
      }),
      transformResponse: (response) => response.data.teammate,
      invalidatesTags: (_result, _error, { id, patchPayload }) => [
        ...getTagsInvalidatedByUpdate(TagType.BasicTeammate, id),
        // The update can move the teammate in or out of a filtered listing —
        // deactivating them takes the row out of the default roster — so the
        // list tag goes along with the entity tag.
        ...getTagsInvalidatedByUpdate(TagType.Teammate, id, {
          inLists: [{ filteredBy: [] }],
        }),
        ...(patchPayload.is_active !== undefined ||
        patchPayload.license_type !== undefined
          ? [buildListTag(TagType.LicenseUtilization, [])]
          : []),
      ],
      async onQueryStarted({ id }, { dispatch, getState, queryFulfilled }) {
        const currentTeammate = selectTeammate(getState() as RootState);
        if (currentTeammate?.id !== id) {
          return;
        }

        try {
          const { data } = await queryFulfilled;
          dispatch(setTeammate(data));
        } catch {
          // Error handling is done by the caller
        }
      },
    }),

    teammateCopilotMessages: builder.query<
      ClientChatMessage[],
      CopilotMessagesRequest,
      {
        data: {
          messages: (
            | UserChatMessage
            | AssistantChatMessage
            | ToolChatMessage
          )[];
        };
      }
    >({
      query: ({ teammateId, chatDetails }) => ({
        method: "GET",
        url: `/teammates/${encodeURIComponent(teammateId)}/copilot-messages`,
        params: chatDetails,
      }),
      transformResponse: (response) =>
        response.data.messages.map(toClientChatMessage),
    }),

    updateMyProfile: builder.mutation<
      Teammate,
      { first_name: string; last_name: string },
      { data: { teammate: Teammate } }
    >({
      query: (body) => ({
        method: "PATCH",
        url: "/teammates/me",
        body,
      }),
      transformResponse: (response) => response.data.teammate,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setTeammate(data));
        } catch {
          // Error handling is done by the caller
        }
      },
    }),

    deleteTeammateCopilotMessages: builder.mutation<
      void,
      CopilotMessagesRequest
    >({
      query: ({ teammateId, chatDetails }) => ({
        method: "DELETE",
        url: `/teammates/${encodeURIComponent(teammateId)}/copilot-messages`,
        params: chatDetails,
      }),
    }),

    teammatesPaginated: builder.infiniteQuery<
      {
        teammates: CompanyTeammateListEntry[];
        nextCursor: string | null;
      },
      { search?: string; includeDeactivated?: boolean },
      string | null,
      {
        data: { teammates: CompanyTeammateListEntry[] };
        next_cursor: string | null;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        method: "GET",
        url: "/teammates/paginated",
        params: {
          cursor: pageParam ?? undefined,
          limit: 20,
          search: queryArg.search ?? undefined,
          include_deactivated: queryArg.includeDeactivated ? "true" : undefined,
        },
      }),
      transformResponse: (response) => ({
        teammates: response.data.teammates,
        nextCursor: response.next_cursor,
      }),
      // `search` and `includeDeactivated` are part of the cache key, so every
      // combination is its own entry. They all provide the same list tag so a
      // mutation invalidates the entries the user isn't looking at too.
      providesTags: (result) =>
        getTagsProvidedByList(
          TagType.Teammate,
          result?.pages.flatMap((page) => page.teammates),
        ),
    }),
  }),
});

export const {
  useAllTeammatesQuery,
  useBasicTeammateQuery,
  useTeammateQuery,
  useUpdateTeammateMutation,
  useUpdateMyProfileMutation,
  useLazyTeammateCopilotMessagesQuery,
  useDeleteTeammateCopilotMessagesMutation,
  useTeammatesPaginatedInfiniteQuery,
} = teammateApi;

export function upsertBasicTeammateQueryData(teammate: BasicTeammate) {
  return teammateApi.util.upsertQueryData(
    "basicTeammate",
    teammate.id,
    teammate,
  );
}
